import { useMemo, useState, useEffect } from "react";
import { UnitList } from "../features/rapidstor/entities/Descriptor";
import Location from "../features/rapidstor/entities/Location";
import {
  matchStandardSizes,
  StandardSizeMatch,
  StandardSizeTable,
} from "../features/rapidstor/entities/StandardSize";
import UnitType from "../features/rapidstor/entities/UnitType";
import { getStandardSizeTable, getStandardSizeTableAsync } from "../config/standardSizes";
import { Filters } from "./useAppliedFilters";

export interface StandardSizeResult {
  /** Matched standard sizes for the active category (units or hardstand) */
  slides: StandardSizeMatch[];
  /** Whether the standard sizes are still being loaded */
  isLoading: boolean;
}

/**
 * Takes the raw unit list from the API and the facility's standard size config,
 * then produces an ordered list of StandardSizeMatch slides for the carousel.
 *
 * Standard sizes with no matching unit types are hidden from the carousel.
 * Only sizes that have at least one unit type are included.
 *
 * This hook now fetches standard sizes from WordPress REST API first,
 * then falls back to hardcoded config if the API is not available.
 */
export function useStandardSizes(
  unitList: UnitList | undefined,
  location: Location,
  filters: Filters = {}
): StandardSizeResult {
  const [isLoading, setIsLoading] = useState(true);
  const [table, setTable] = useState<StandardSizeTable | null>(null);

  // Fetch standard sizes from WordPress on mount or location change
  useEffect(() => {
    let cancelled = false;

    const fetchSizes = async () => {
      setIsLoading(true);
      try {
        const sizes = await getStandardSizeTableAsync(location.locationCode);
        if (!cancelled) {
          setTable(sizes);
        }
      } catch (error) {
        console.error("Failed to fetch standard sizes:", error);
        // Fall back to hardcoded config only on failure
        if (!cancelled) {
          setTable(getStandardSizeTable(location.locationCode));
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchSizes();

    return () => {
      cancelled = true;
    };
  }, [location.locationCode]);

  const slides = useMemo(() => {
    if (!unitList || !table) return [];

    const definitions = table.units;
    const groundFloor = location.rapidstor.groundFloor;

    // Apply questionnaire-based sTypeName.re filter to unit types.
    // "Parking"  → include only units where typeName matches "Parking"
    // "!Parking" → exclude units where typeName matches "Parking"
    let filteredUnitTypes = unitList.unitTypes;
    let filteredDescriptors = unitList.descriptors;
    const typeNameFilter = filters["sTypeName.re"];
    if (typeof typeNameFilter === "string" && typeNameFilter.length > 0) {
      const negated = typeNameFilter.startsWith("!");
      const pattern = negated ? typeNameFilter.slice(1) : typeNameFilter;
      const regex = new RegExp(pattern, "i");

      filteredUnitTypes = unitList.unitTypes.filter((ut) =>
        negated ? !regex.test(ut.typeName) : regex.test(ut.typeName)
      );

      // Also filter each descriptor's unitTypes so matchStandardSizes sees
      // consistent data, then drop descriptors with no remaining unit types.
      const filteredIds = new Set(filteredUnitTypes.map((ut) => ut._id));
      filteredDescriptors = unitList.descriptors
        .map((d) => ({
          ...d,
          unitTypes: d.unitTypes.filter((ut) => filteredIds.has(ut._id)),
        }))
        .filter((d) => d.unitTypes.length > 0);
    }

    // Build a preference function so distributeUnitsToGroups picks units
    // that meet the questionnaire's area/length criteria over cheaper ones that don't.
    const minArea = filters["dcArea.min"] as number | undefined;
    const minLength = filters["dcLength.min"] as number | undefined;
    const preferFn =
      minArea !== undefined || minLength !== undefined
        ? (ut: UnitType) => {
            if (minArea !== undefined && ut.area < minArea) return false;
            if (minLength !== undefined && ut.length < minLength) return false;
            return true;
          }
        : undefined;

    return matchStandardSizes(
      filteredDescriptors,
      filteredUnitTypes,
      definitions,
      groundFloor,
      preferFn
    ).filter((match) => match.unitTypes.length > 0);
  }, [unitList, table, location.rapidstor.groundFloor, filters]);

  return { slides, isLoading };
}
